import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { normalizeImageUrl } from '../components/Post';
import { apiService } from '../utils/api';
import './ReviewDetails.css';

export default function ReviewDetails() {
  const { target_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDetails();
  }, [target_id]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // apiService はレスポンスボディを直接返す（config.API_BASE_URL を使用）
      const response = await apiService.get(`/api/reviews-ranking/${target_id}/details`);
      const result = response?.target_info ? response : (response?.data || response || {});
      setData(result);
    } catch (err) {
      console.error('Fetch details error:', err);
      setError(err.response?.data?.error || err.message || '詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="review-details loading">読み込み中...</div>;
  }

  if (error) {
    return (
      <div className="review-details error">
        <p>エラーが発生しました: {error}</p>
        <button onClick={() => navigate('/reviews-ranking')}>ランキングに戻る</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="review-details">
        <p>データが見つかりません</p>
        <button onClick={() => navigate('/reviews-ranking')}>ランキングに戻る</button>
      </div>
    );
  }

  const {
    target_info,
    current_score,
    monthly_trend,
    feedback_analysis
  } = data;

  const chartColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c'];

  return (
    <div className="review-details">
      <div className="details-header">
        <button className="back-btn" onClick={() => navigate('/reviews-ranking')}>
          ← ランキングに戻る
        </button>
      </div>

      {/* ターゲット情報セクション */}
      <div className="target-info-card">
        <div className="target-header">
          {target_info.target_avatar ? (
            <img src={normalizeImageUrl(target_info.target_avatar)} alt={target_info.target_name} className="target-avatar" />
          ) : (
            <div className="target-avatar target-avatar-placeholder">
              {(target_info.target_name || '?').charAt(0)}
            </div>
          )}
          <div className="target-details">
            <h1>{target_info.target_name}</h1>
            <p className="target-type">
              {target_info.target_type === 'corporate' ? '企業' : 'ユーザー'}
            </p>
          </div>
        </div>

        {/* スコア情報 */}
        <div className="score-info">
          <div className="score-box">
            <span className="score-label">ランク</span>
            <span className="score-value">#{current_score.rank}</span>
          </div>
          <div className="score-box">
            <span className="score-label">平均評価</span>
            <span className="score-value">{current_score.avg_rating.toFixed(2)} ⭐</span>
          </div>
          <div className="score-box">
            <span className="score-label">Bayesian スコア</span>
            <span className="score-value">{current_score.bayesian_score.toFixed(2)}</span>
          </div>
          <div className="score-box">
            <span className="score-label">総レビュー数</span>
            <span className="score-value">{current_score.total_reviews}</span>
          </div>
          <div className="score-box">
            <span className="score-label">3ヶ月内</span>
            <span className="score-value">{current_score.recent_reviews_3m}</span>
          </div>
        </div>
      </div>

      {/* 月別トレンドチャート */}
      {monthly_trend && monthly_trend.length > 0 && (
        <div className="chart-container">
          <h2>📈 月別トレンド</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthly_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" label={{ value: 'Bayesian スコア', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" label={{ value: '平均評価', angle: 90, position: 'insideRight' }} />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="bayesian_score"
                stroke="#8884d8"
                name="Bayesian スコア"
                connectNulls
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avg_rating"
                stroke="#82ca9d"
                name="平均評価"
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* レーティング分布 */}
      {feedback_analysis && feedback_analysis.ratings_distribution && (
        <div className="chart-container">
          <h2>⭐ レーティング分布</h2>
          <div className="ratings-distribution">
            <div className="distribution-chart">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: '5つ星', value: feedback_analysis.ratings_distribution[5] || 0 },
                      { name: '4つ星', value: feedback_analysis.ratings_distribution[4] || 0 },
                      { name: '3つ星', value: feedback_analysis.ratings_distribution[3] || 0 },
                      { name: '2つ星', value: feedback_analysis.ratings_distribution[2] || 0 },
                      { name: '1つ星', value: feedback_analysis.ratings_distribution[1] || 0 }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartColors.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="distribution-table">
              <table>
                <thead>
                  <tr>
                    <th>レーティング</th>
                    <th>件数</th>
                    <th>割合</th>
                  </tr>
                </thead>
                <tbody>
                  {[5, 4, 3, 2, 1].map(rating => {
                    const count = feedback_analysis.ratings_distribution[rating] || 0;
                    const total = Object.values(feedback_analysis.ratings_distribution).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    
                    return (
                      <tr key={rating}>
                        <td>{'⭐'.repeat(rating)}</td>
                        <td>{count}</td>
                        <td>{percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* キーワード分析 */}
      {feedback_analysis && feedback_analysis.top_keywords && feedback_analysis.top_keywords.length > 0 && (
        <div className="keywords-container">
          <h2>🔑 よく出現するキーワード</h2>
          <div className="keywords-list">
            {feedback_analysis.top_keywords.map((kw, idx) => (
              <div key={idx} className="keyword-badge">
                <span className="keyword-text">{kw.keyword}</span>
                <span className="keyword-count">({kw.count})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 最新レビュー */}
      {feedback_analysis && feedback_analysis.recent_reviews && feedback_analysis.recent_reviews.length > 0 && (
        <div className="recent-reviews-container">
          <h2>💬 最新レビュー</h2>
          <div className="reviews-list">
            {feedback_analysis.recent_reviews.map(review => (
              <div key={review.id} className="review-item">
                <div className="review-header">
                  <div className="reviewer-info">
                    {review.reviewer_avatar ? (
                      <img src={normalizeImageUrl(review.reviewer_avatar)} alt={review.reviewer_name} className="reviewer-avatar" />
                    ) : (
                      <div className="reviewer-avatar reviewer-avatar-placeholder">
                        {(review.reviewer_name || '?').charAt(0)}
                      </div>
                    )}
                    <span className="reviewer-name">{review.reviewer_name}</span>
                  </div>
                  <span className="review-rating">{'⭐'.repeat(review.rating)}</span>
                </div>
                <div className="review-text">{review.text}</div>
                <div className="review-date">{new Date(review.created_at).toLocaleDateString('ja-JP')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
